// LeadApplicantSelector — choose which applicant is the lead on a draft (§3).
//
// The lead applicant is listed first on the lease (DocuSeal role Resident1), so
// staff need to pick them explicitly when the AppFolio "primary" isn't the right
// person to head the document. Default = the first applicant to apply (group
// order); see buildSubmitters' default-selection logic. It says nothing about
// signing order: every resident is invited to sign the moment the lease is sent
// (2026-08-28), and nobody fills anything in on the document.
//
// This control is presentational: it persists the choice by calling `onChange`,
// which the host (ConfigurePage) wires to the draft form-state update
// (`update('leadApplicantId', id)`) — going through the existing autosave path
// so the selection lands on the server like any other field.

interface ApplicantOption {
  id: string;
  firstName: string;
  lastName: string;
  isPrimary: boolean;
  email: string | null;
  /** yyyy-mm-dd or null; drives the missing-DOB hint (every resident must sign). */
  dateOfBirth: string | null;
}

interface LeadApplicantSelectorProps {
  applicants: ApplicantOption[];
  /** Persisted lead id; when undefined, the first applicant is the effective lead. */
  leadApplicantId: string | undefined;
  onChange: (id: string) => void;
  disabled?: boolean;
}

export function LeadApplicantSelector({
  applicants,
  leadApplicantId,
  onChange,
  disabled = false,
}: LeadApplicantSelectorProps): React.JSX.Element | null {
  if (applicants.length === 0) return null;

  // The effective lead falls back to the first applicant in group order when
  // no explicit selection has been persisted yet (mirrors buildSubmitters).
  const effectiveLeadId = leadApplicantId ?? applicants[0]?.id;

  // A single applicant has no decision to make; show a read-only line so staff
  // still see who heads the lease without an interactive control.
  if (applicants.length === 1) {
    const only = applicants[0]!;
    return (
      <section className="card">
        <div className="card-header">
          <h3 className="card-title">Lead applicant</h3>
        </div>
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)' }}>
          {`${only.firstName} ${only.lastName}`.trim()} is the only applicant — listed on the lease as Resident1.
        </div>
      </section>
    );
  }

  return (
    <section className="card">
      <div className="card-header">
        <h3 className="card-title">Lead applicant</h3>
      </div>
      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', marginBottom: 'var(--space-3)' }}>
        Listed first on the lease as Resident1. Every resident is invited to sign at the same time — nobody fills anything in. Defaults to the first applicant to apply.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        {applicants.map((a) => {
          const name = `${a.firstName} ${a.lastName}`.trim();
          const checked = a.id === effectiveLeadId;
          return (
            <label key={a.id} className="choice" style={{ alignItems: 'flex-start' }}>
              <input
                type="radio"
                className="radio"
                name="lead-applicant"
                checked={checked}
                disabled={disabled}
                onChange={() => onChange(a.id)}
              />
              <span className="choice-meta">
                <span>
                  {name}
                  {a.isPrimary && (
                    <span className="badge" data-size="sm" style={{ marginLeft: 'var(--space-2)' }}>
                      AppFolio primary
                    </span>
                  )}
                </span>
                <span className="choice-helper">
                  {a.email ?? 'no email on file'}
                  {a.dateOfBirth ? '' : ' · no date of birth on file'}
                </span>
              </span>
            </label>
          );
        })}
      </div>
    </section>
  );
}
